// src/dossiers/save.js — „Запази в досие" от калкулатор.
//
// Преработка (25.08.2026, решение на Макс): лицето Е досието и всичко върви
// през АКТИВНАТА ФИРМА — както при фактурите. Диалогът НЕ пита за фирма и
// няма ниво „папка": избираш човек (или пишеш ново име) и готово.
//
// Записът е СНИМКА на видяното: входните полета (етикет → стойност),
// редовете на резултата и правното основание от страничната колона. Снима
// се ДОМ-ът, а не вътрешното състояние — така ЕДИН код работи за всички
// калкулатори „за лице", без всеки да декларира какво да се пази.

import { el, field, input } from '../shared/dom.js';
import { toast } from '../shared/toast.js';
import { api } from '../shared/api.js';
import { openModal, closeModal } from '../shared/modal.js';
import { contactPicker } from '../shared/pickers.js';
import { state, cq } from '../shared/state.js';

// Снимката на панела: { inputs, result, law }.
export function capturePanel(root) {
  const inputs = [];
  // Полетата минават през field() (етикет ↔ поле) — двойката е и снимката.
  root.querySelectorAll('.calc-panel .field').forEach((f) => {
    // Група радио-бутони (напр. „брутна/нетна сума" в „Граждански договор"):
    // името е в aria-label, стойността — надписът на избрания бутон.
    if (f.getAttribute('role') === 'radiogroup') {
      const on = f.querySelector('input[type="radio"]:checked');
      const name = f.getAttribute('aria-label');
      if (on && name) inputs.push({ label: name, value: ((on.closest('label') || {}).textContent || on.value).trim() });
      return;
    }
    const lbl = f.querySelector('label');
    const n = f.querySelector('input, select, textarea');
    if (!lbl || !n) return;
    // Скрито условно поле (напр. „по-нископлатена работа" без отметка) не влиза.
    const cs = (f.ownerDocument.defaultView || window).getComputedStyle(f);
    if (cs.display === 'none') return;
    let value = '';
    if (n.type === 'checkbox') { if (!n.checked) return; value = 'да'; }
    else if (n.tagName === 'SELECT') value = (n.selectedOptions && n.selectedOptions[0] && n.selectedOptions[0].textContent) || '';
    else value = n.value;
    if (!String(value).trim()) return; // празното поле не се пази
    // Иконката § (lawHint) живее в етикета — нейният текст не е име на полето.
    const lblText = [...lbl.childNodes].filter((x) => x.nodeType === 3).map((x) => x.textContent).join('').trim() || lbl.textContent;
    inputs.push({ label: lblText, value: String(value) });
  });

  const result = [];
  // Редовете на резултата, както ги показва панелът (label / value / big).
  root.querySelectorAll('.calc-line').forEach((ln) => {
    const spans = [...ln.children].filter((x) => x.tagName === 'SPAN');
    if (spans.length < 2) return; // голите подсказки („Попълнете…") не са резултат
    result.push({ label: spans[0].textContent, value: spans[1].textContent, big: ln.classList.contains('big') });
  });
  // Сборната таблица на обезщетенията (.sev-results): ред = обезщетение,
  // последната колона е сумата; tfoot е общият сбор.
  root.querySelectorAll('.sev-results tbody tr, .sev-results tfoot tr').forEach((tr) => {
    const cells = [...tr.children];
    if (cells.length < 2) return;
    const label = cells[0].textContent.trim();
    const value = cells[cells.length - 1].textContent.trim();
    if (!label || !value) return;
    result.push({ label, value, big: tr.parentElement && tr.parentElement.tagName === 'TFOOT' });
  });

  // Правното основание от страничната колона — копие, за да остане вярно
  // и след промяна на закона.
  let law = null;
  const box = root.querySelector('.calc-law');
  if (box) {
    const when = [...box.querySelectorAll('ul li')].map((li) => li.textContent);
    const quotes = [...box.querySelectorAll('.law-quote')].map((q) => ({
      ref: (q.querySelector('.law-ref') || {}).textContent || '',
      text: ((q.querySelector('blockquote') || {}).textContent || '').replace(/^„/, '').replace(/[“"]$/, ''),
    }));
    if (when.length || quotes.length) law = { when, quotes };
  }
  return { inputs, result, law };
}

// Диалогът: човек (или ново име) — активната фирма, без други стъпки.
export function saveCalcToDossier(def, root) {
  const snap = capturePanel(root);
  if (!snap.result.length) {
    toast('Попълнете сметката — резултатът още е празен и няма какво да се запише.', true);
    return;
  }
  // Като при фактурите: работи се в контекста на ИЗБРАНАТА фирма.
  if (!state().activeCompany) { toast('Първо изберете фирма — досиетата са по фирма, като фактурите.', true); return; }

  // Панел с избрано лице (напр. „Получател на дохода" в чл. 55) казва къде
  // да се запише БЕЗ диалог — мостът е _dossierPreset, по модела на
  // _dividend. Без избрано лице се пада към стандартния диалог.
  if (typeof root._dossierPreset === 'function') {
    Promise.resolve(root._dossierPreset())
      .then((preset) => {
        if (!(preset && preset.personId)) { openSaveDialog(def, snap); return; }
        return api('/dossiers/' + preset.personId + '/entries', {
          method: 'POST',
          body: JSON.stringify({ calc_key: def.key, calc_label: def.label, inputs: snap.inputs, result: snap.result, law: snap.law }),
        }).then(() => toast('Сметката е записана в досието на ' + (preset.name || 'лицето') + '.'));
      })
      .catch((e) => toast(e.message, true));
    return;
  }
  openSaveDialog(def, snap);
}

// Стандартният диалог: човек (или ново име) — активната фирма.
function openSaveDialog(def, snap) {

  const personSel = el('select', {});
  const nameInput = input({ value: '', placeholder: 'Иван Петров Иванов' });
  const nameField = field('Име на новото досие (лице)', nameInput);
  const contactHidden = el('input', { type: 'hidden', value: '' });
  const contactField = field('Клиент (по избор)', el('div', {}, contactHidden));
  const NEW = '__new__';

  async function loadPersons() {
    let persons = [];
    try { persons = await api('/dossiers' + cq()); } catch (e) {}
    personSel.innerHTML = '';
    persons.forEach((p) => personSel.appendChild(el('option', { value: p.id, text: p.full_name })));
    personSel.appendChild(el('option', { value: NEW, text: '+ Ново досие (ново лице)' }));
    syncPerson();
  }
  async function syncPerson() {
    const isNew = personSel.value === NEW;
    nameField.style.display = isNew ? '' : 'none';
    contactField.style.display = isNew ? '' : 'none';
    if (isNew && !contactField.dataset.loaded) {
      contactField.dataset.loaded = '1';
      let contacts = [];
      try { contacts = await api('/contacts' + cq()); } catch (e) {}
      const wrap = contactField.querySelector('div');
      wrap.innerHTML = '';
      const picker = contactPicker(contacts, contactHidden);
      wrap.appendChild(picker);
      // Етикетът да сочи ВИДИМОТО поле на търсачката, не скрития input.
      const search = picker.querySelector('input[type="search"], input');
      const lbl = contactField.querySelector('label');
      if (search && lbl) { if (!search.id) search.id = 'dossier-contact-' + Date.now(); lbl.setAttribute('for', search.id); }
    }
  }
  personSel.addEventListener('change', syncPerson);

  const save = async () => {
    try {
      let personId = personSel.value;
      if (!personId) { toast('Няма избрано досие.', true); return; }
      if (personId === NEW) {
        if (!nameInput.value.trim()) { toast('Въведете име на лицето.', true); nameInput.focus(); return; }
        const p = await api('/dossiers', { method: 'POST', body: JSON.stringify({
          company_id: state().activeCompany,
          full_name: nameInput.value.trim(),
          contact_id: contactHidden.value || null,
        }) });
        personId = p.id;
      }
      await api('/dossiers/' + personId + '/entries', {
        method: 'POST',
        body: JSON.stringify({ calc_key: def.key, calc_label: def.label, inputs: snap.inputs, result: snap.result, law: snap.law }),
      });
      closeModal();
      toast('Сметката е записана в досието.');
    } catch (e) { toast(e.message, true); }
  };

  openModal({
    title: 'Запази в досие: ' + def.label,
    body: el('div', {},
      el('p', { style: 'margin:0 0 10px;color:var(--muted);font-size:13px', text: 'Записват се въведените данни, резултатът и правното основание — после излизат в PDF от раздел „Досиета". Досието е човек от избраната фирма.' }),
      field('Досие (лице)', personSel),
      nameField,
      contactField),
    foot: el('div', {},
      el('button', { class: 'btn ghost', text: 'Отказ', onClick: closeModal }),
      el('button', { class: 'btn', text: 'Запази', onClick: save, style: 'margin-left:8px' })),
  });
  loadPersons();
}
